"use client";

import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import { useFeaturedBanners } from "@/hooks/useMetadata";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FeaturedBannerV2 } from "./featured-banner-v2";

interface FeaturedBannersV2Props {
  className?: string;
  limit?: number;
  showHeading?: boolean;
}

export function FeaturedBannersV2({
  className = "",
  limit = 10,
  showHeading = false,
}: FeaturedBannersV2Props) {
  const { data: banners = [], isLoading: loading } = useFeaturedBanners();
  const [currentIndex, setCurrentIndex] = useState(0);

  const limitedBanners = banners.slice(0, limit);

  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const resumeAutoplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoplayDelay = 10000;
  const overlayFrozen = useTheaterOverlayFrozen();
  const overlayFrozenRef = useRef(overlayFrozen);
  overlayFrozenRef.current = overlayFrozen;

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    containScroll: "trimSnaps",
    dragFree: false,
    skipSnaps: false,
    duration: 45,
    slidesToScroll: 1,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  const startAutoplay = useCallback(() => {
    if (autoplayRef.current || !emblaApi || overlayFrozenRef.current) return;
    autoplayRef.current = setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollTo(0);
      }
    }, autoplayDelay);
  }, [emblaApi]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
    if (resumeAutoplayTimeoutRef.current) {
      clearTimeout(resumeAutoplayTimeoutRef.current);
      resumeAutoplayTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    if (limitedBanners.length > 1) startAutoplay();
    const handlePointerDown = () => stopAutoplay();
    const handlePointerUp = () => {
      resumeAutoplayTimeoutRef.current = setTimeout(startAutoplay, 3000);
    };
    emblaApi.on("pointerDown", handlePointerDown);
    emblaApi.on("pointerUp", handlePointerUp);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerDown", handlePointerDown);
      emblaApi.off("pointerUp", handlePointerUp);
      stopAutoplay();
    };
  }, [emblaApi, limitedBanners.length, onSelect, startAutoplay, stopAutoplay]);

  useEffect(() => {
    if (overlayFrozen) {
      stopAutoplay();
      return;
    }
    if (emblaApi && limitedBanners.length > 1) {
      startAutoplay();
    }
  }, [overlayFrozen, emblaApi, limitedBanners.length, startAutoplay, stopAutoplay]);

  if (!loading && limitedBanners.length === 0) return null;

  if (!loading && limitedBanners.length === 1) {
    return (
      <div className={`py-4 w-full ${className}`}>
        {showHeading && (
          <h2 className="text-2xl font-bold text-brand-purple mb-6">Featured Games</h2>
        )}
        <div className="flex justify-center animate-fade-scale-in">
          <FeaturedBannerV2 banner={limitedBanners[0]} className="w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className={`pt-8 pb-8 px-0 ${className}`}>
      <h2 className="text-3xl md:text-4xl lg:text-5xl text-white mb-6 text-center">
        Featured Games
      </h2>
      {loading ? (
        <div className="flex justify-center">
          <div
            className="rounded-3xl bg-brand-purple relative w-full overflow-hidden p-0"
            style={{ aspectRatio: "960/320" }}
          >
            <div className="w-full h-full shimmer-gradient" />
          </div>
        </div>
      ) : (
        <div className="relative">
          <div className="embla overflow-hidden p-0" ref={emblaRef}>
            <div className="embla__container flex touch-pan-y">
              {limitedBanners.map((banner, index) => (
                <div key={banner.id} className="embla__slide relative">
                  <div
                    className="transition-all duration-500 ease-out"
                    style={{
                      opacity: index === currentIndex ? 1 : 0.6,
                      transform: index === currentIndex ? "scale(1)" : "scale(.96)",
                    }}
                  >
                    <FeaturedBannerV2 banner={banner} className="w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .shimmer-gradient {
          position: relative;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, rgb(var(--brand-purple) / 0.9) 0%, rgb(var(--brand-indigo-soft) / 0.15) 50%, rgb(var(--brand-purple) / 0.9) 100%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite linear;
          border-radius: 1.5rem;
        }
        @keyframes shimmer { 0% { background-position: -100% 0; } 100% { background-position: 100% 0; } }
        @keyframes fadeScaleIn { 0% { opacity: 0; transform: scale(0.97); } 100% { opacity: 1; transform: scale(1); } }
        .animate-fade-scale-in { animation: fadeScaleIn 2.8s ease-out forwards; }
        .embla { --slide-spacing: 0.5rem; --slide-size: 84%; --slide-height: 19rem; padding: 0; will-change: transform; }
        .embla__container { backface-visibility: hidden; display: flex; touch-action: pan-y; margin: 0; width: 100%; will-change: transform; -webkit-transform-style: preserve-3d; transform-style: preserve-3d; }
        .embla__slide { will-change: transform; -webkit-backface-visibility: hidden; backface-visibility: hidden; width: var(--slide-size); flex: 0 0 var(--slide-size); min-width: 0; padding-left: calc(var(--slide-spacing) / 2); padding-right: calc(var(--slide-spacing) / 2); }
        @media (min-width: 768px) { .embla { --slide-spacing: 1rem; --slide-size: 88%; } }
      `}</style>
    </div>
  );
}

export default FeaturedBannersV2;
